class NsArticleSlideshow extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.autoplayDelay = 5000;
  }

  connectedCallback() {
    this.initSwiper();
    this.setupThemeEditorListeners();
  }

  initSwiper() {
    const progressFill = this.querySelector('.ns-progress-fill');
    const swiperEl = this.querySelector('.ns-slideshow-swiper');

    // Get autoplay setting from attribute
    const autoplayAttr = this.getAttribute('autoplay');
    const autoplayConfig = autoplayAttr ? {
      delay: parseInt(autoplayAttr) || 5000,
      disableOnInteraction: false,
    } : false;

    const showProgressBar = this.hasAttribute('show-progress-bar');

    this.swiper = new Swiper(swiperEl, {
      effect: 'fade',
      fadeEffect: { crossFade: true },
      loop: true,
      speed: 500,
      autoplay: autoplayConfig,
      pagination: {
        el: this.querySelector('.ns-swiper-pagination'),
        clickable: true,
      },
      navigation: {
        nextEl: this.querySelector('.ns-swiper-button-next'),
        prevEl: this.querySelector('.ns-swiper-button-prev'),
      },
      on: {
        init: (swiper) => {
          swiper.slides[swiper.activeIndex].style.opacity = '1';
          if (progressFill && autoplayConfig && showProgressBar) {
            progressFill.style.transform = 'scaleX(0)';
          }
        },
        autoplayTimeLeft: (swiper, time, progress) => {
          if (progressFill && autoplayConfig && showProgressBar) {
            const elapsedProgress = 1 - progress;
            progressFill.style.transform = `scaleX(${elapsedProgress})`;
          }
        }
      }
    });
  }

  setupThemeEditorListeners() {
    // Use Shopify design mode detection
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        this.pauseAutoplay();
        this.goToSlideByBlockId(event.detail.blockId);

        // Force Swiper to update after a short delay
        setTimeout(() => {
          if (this.swiper) {
            this.swiper.update();
            this.swiper.slideToLoop(this.swiper.activeIndex); // Force refresh current slide
          }
        }, 150);
      });

      this.addEventListener('shopify:block:deselect', () => {
        this.resumeAutoplay();
      });

      this.addEventListener('shopify:section:select', () => {
        this.pauseAutoplay();
      });

      this.addEventListener('shopify:section:deselect', () => {
        this.resumeAutoplay();
      });
    }
  }

  goToSlideByBlockId(blockId) {
    if (!this.swiper) return;
    
    const slides = this.querySelectorAll('.ns-swiper-slide');
    const slideIndex = Array.from(slides).findIndex(slide => {
      return slide.getAttribute('data-block-id') === blockId;
    });

    if (slideIndex !== -1) {
      this.swiper.slideTo(slideIndex);
    }
  }

  pauseAutoplay() {
    const showProgressBar = this.hasAttribute('show-progress-bar');
    if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.running && showProgressBar) {
      this.swiper.autoplay.stop();
    }
  }

  resumeAutoplay() {
    const autoplayAttr = this.getAttribute('autoplay');
    const showProgressBar = this.hasAttribute('show-progress-bar');
    if (this.swiper && autoplayAttr && showProgressBar) {
      this.swiper.autoplay.start();
    }
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy();
    }
  }
}

customElements.define('ns-article-slideshow', NsArticleSlideshow);



class NsFeaturedWork extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    this.initSwiper();
    this.setupThemeEditorListeners();
  }

  initSwiper() {
    const swiperEl = this.querySelector('.ns-featured-work-swiper');

    // Check if we have enough slides to initialize Swiper
    const slides = this.querySelectorAll('.ns-featured-work-swiper-slide');
    if (slides.length <= 1) {
      return;
    }

    this.swiper = new Swiper(swiperEl, {
      speed: 500,
      slidesPerView: "auto",
      centeredSlides: true,
      slideToClickedSlide: true,
      breakpoints: {
        768: {
          spaceBetween: 40
        }
      },
      navigation: {
        nextEl: this.querySelector('.ns-swiper-button-next'),
        prevEl: this.querySelector('.ns-swiper-button-prev'),
      }
    });
  }

  setupThemeEditorListeners() {
    // Use Shopify design mode detection
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        this.goToSlideByBlockId(event.detail.blockId);

        // Force Swiper to update after a short delay
        setTimeout(() => {
          if (this.swiper) {
            this.swiper.update();
            this.swiper.slideToLoop(this.swiper.activeIndex); // Force refresh current slide
          }
        }, 150);
      });
    }
  }

  goToSlideByBlockId(blockId) {
    if (!this.swiper) return;
    
    const slides = this.querySelectorAll('.ns-featured-work-swiper-slide');
    const slideIndex = Array.from(slides).findIndex(slide => {
      return slide.getAttribute('data-block-id') === blockId;
    });

    if (slideIndex !== -1) {
      this.swiper.slideTo(slideIndex);
    }
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy();
    }
  }
}

customElements.define('ns-featured-work', NsFeaturedWork);


class NSCollectionCollapsible extends HTMLElement {
  constructor() {
    super();
    this.isTransitioning = false;
  }
  
  connectedCallback() {
    // Use setTimeout to ensure children are parsed
    setTimeout(() => {
      this.isOpen = this.hasAttribute('open');
      this.summaryElement = this.querySelector('.ns-collection-collapsible__summary');
      this.contentElement = this.querySelector('.ns-collection-collapsible__content');
      
      if (!this.summaryElement || !this.contentElement) {
        console.warn('Missing required elements');
        return;
      }
      
      this.summaryElement.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
      
      // Set initial height
      if (this.isOpen) {
        this.style.height = 'auto';
      } else {
        this.setPeekHeight();
      }

      // Add resize observer to handle window resizing
      this.setupResizeObserver();
      
      // Setup theme editor listeners
      this.setupThemeEditorListeners();
    });
  }
  
  getPeekHeight() {
    const computedStyle = getComputedStyle(this);
    const peekHeightValue = computedStyle.getPropertyValue('--peek-height').trim();
    return parseInt(peekHeightValue) || 60;
  }

  setPeekHeight() {
    // Always get fresh peek height from CSS
    const currentPeekHeight = this.getPeekHeight();
    this.style.height = `${currentPeekHeight}px`;
  }
  
  static get observedAttributes() {
    return ['open'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open' && this.summaryElement) {
      this.isOpen = newValue !== null;
      
      if (!this.isTransitioning) {
        if (this.isOpen) {
          this.expand();
        } else {
          this.collapse();
        }
      }
    }
  }
  
  async toggle() {
    if (this.isTransitioning) return;
    
    if (this.isOpen) {
      this.removeAttribute('open');
      await this.collapse();
    } else {
      this.setAttribute('open', '');
      await this.expand();
    }
  }
  
  async expand() {
    this.isTransitioning = true;
    const startHeight = this.clientHeight;
    this.isOpen = true;
    
    this.style.height = 'auto';
    const endHeight = this.scrollHeight;
    
    // Reset to start height for animation
    this.style.height = `${startHeight}px`;
    
    await this.animateHeight(startHeight, endHeight, 250, 'ease');
    
    // Clear the animation and set to auto
    this.style.height = 'auto';
    this.isTransitioning = false;
  }
  
  async collapse() {
    this.isTransitioning = true;
    const startHeight = this.clientHeight;
    // Always get fresh peek height for collapse
    const endHeight = this.getPeekHeight();
    this.isOpen = false;
    
    await this.animateHeight(startHeight, endHeight, 250, 'ease');
    
    // Use setPeekHeight instead of hardcoded value
    this.setPeekHeight();
    this.isTransitioning = false;
  }
  
  animateHeight(start, end, duration, easing) {
    return new Promise(resolve => {
      const animation = this.animate(
        { height: [`${start}px`, `${end}px`] },
        { 
          duration, 
          easing,
        }
      );
      animation.onfinish = () => {
        animation.cancel();
        resolve();
      };
    });
  }

  setupResizeObserver() {
    const resizeObserver = new ResizeObserver(entries => {
      if (this.isOpen && !this.isTransitioning) {
        // If open and not animating, ensure height is auto
        this.style.height = 'auto';
      } else if (!this.isOpen) {
        // If closed, update to current peek height
        this.setPeekHeight();
      }
    });

    if (this.contentElement) {
      resizeObserver.observe(this.contentElement);
    }

    window.addEventListener('resize', () => {
      if (!this.isOpen) {
        this.setPeekHeight();
      }
    });
  }

  setupThemeEditorListeners() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        if (!this.isOpen) {
          this.setAttribute('open', '');
          this.expand();
        }
      });

      // Optional: Close when another block is selected
      this.addEventListener('shopify:block:deselect', (event) => {
        if (this.isOpen) {
          this.removeAttribute('open');
          this.collapse();
        }
      });

      // Listen for section selection to handle multiple collapsibles
      const section = this.closest('.shopify-section');
      if (section) {
        section.addEventListener('shopify:section:select', (event) => {
          // You might want to open the first collapsible when section is selected
          // or handle section-level selection logic here
        });
      }
    }
  }
}

// Register the custom element
if (!customElements.get('ns-collection-collapsible')) {
  customElements.define('ns-collection-collapsible', NSCollectionCollapsible);
}


class NsProductGallery extends HTMLElement {
  constructor() {
    super();
    this._onVariantChangedListener = this._onVariantChanged.bind(this);
  }

  connectedCallback() {
    document.forms[this.getAttribute("form")]?.addEventListener("variant:change", this._onVariantChangedListener);

    this.carousel = this.querySelector(".swiper");
    this._allSlides = Array.from(this.carousel.querySelectorAll('.swiper-slide'));

    // Read the initial index from the DOM
    const wrapper = this.carousel.querySelector('.swiper-wrapper');
    const initialIndex = parseInt(wrapper.getAttribute('initial-index')) || 0;
    this._filteredSlides = [...this._allSlides]; // Assume no filtering on initial load

    // Initialize Swiper
    this._swiper = new Swiper(this.carousel, {
      loop: false,
      initialSlide: initialIndex,
      pagination: {
        el: '.ns-swiper-pagination',
        clickable: true
      },
      navigation: {
        nextEl: '.ns-swiper-button-next',
        prevEl: '.ns-swiper-button-prev',
      }
    });

    this.addEventListener("carousel:filter", this._filterItems);

    this.carousel.select = this.select.bind(this);
  }

  disconnectedCallback() {
    document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    this.removeEventListener("carousel:filter", this._filterItems);
  }

  select(index, options = { animate: true }) {
    if (!this._swiper) return;

    const speed = options.animate === false ? 0 : 300;

    // Sanity check: skip if filtered index is out of range
    if (index < 0 || index >= this._filteredSlides.length) return;

    this._swiper.slideTo(index, speed);
  }

  _filterItems(event) {
    const filteredIndexes = event.detail.filteredIndexes || [];

    // Destroy existing swiper instance
    if (this._swiper) {
      this._swiper.destroy(true, true);
    }

    const wrapper = this.carousel.querySelector('.swiper-wrapper');
    wrapper.innerHTML = '';

    // Rebuild slide list
    this._filteredSlides = this._allSlides.filter((slide, index) => !filteredIndexes.includes(index));
    this._filteredSlides.forEach(slide => {
      slide.removeAttribute('hidden');
      wrapper.appendChild(slide);
    });

    // Re-select navigation and pagination elements in case they were detached
    const pagination = this.querySelector('.ns-swiper-pagination');
    const nextButton = this.querySelector('.ns-swiper-button-next');
    const prevButton = this.querySelector('.ns-swiper-button-prev');

    // Re-initialize Swiper with updated DOM
    this._swiper = new Swiper(this.carousel, {
      loop: false,
      pagination: {
        el: pagination,
        clickable: true,
      },
      navigation: {
        nextEl: nextButton,
        prevEl: prevButton,
      }
    });

    // Re-select previously selected index if available
    if (typeof this._pendingSelectIndex === 'number') {
      const newIndex = this._filteredSlides.findIndex(slide =>
        parseInt(slide.dataset.index) === this._pendingSelectIndex
      );

      if (newIndex !== -1) {
        this.select(newIndex, { animate: false });
      }

      delete this._pendingSelectIndex;
    }
  }


  _onVariantChanged(event) {
    const filteredIndexes = this._getFilteredMediaIndexes(event.detail.product, event.detail.variant);

    const selectIndex = event.detail.variant?.featured_media?.position;

    // Store it temporarily to use after filtering
    this._pendingSelectIndex = selectIndex;

    this.carousel.dispatchEvent(new CustomEvent("carousel:filter", {
      bubbles: true,
      detail: { filteredIndexes }
    }));
  }

  _getFilteredMediaIndexes(product, variant) {
    const filteredMediaIds = [];

    product["media"].forEach((media) => {
      let matchMedia = variant["featured_media"] && media["position"] === variant["featured_media"]["position"];

      if (media["alt"]?.includes("#") && media["alt"] !== product["title"]) {
        if (!matchMedia) {
          const altParts = media["alt"].split("#"), mediaGroupParts = altParts.pop().split("_");

          product["options"].forEach((option) => {
            if (option["name"].toLowerCase() === mediaGroupParts[0].toLowerCase()) {
              if (variant["options"][option["position"] - 1].toLowerCase() !== mediaGroupParts[1].trim().toLowerCase()) {
                filteredMediaIds.push(media["position"] - 1);
              }
            }
          });
        }
      }
    });

    return filteredMediaIds;
  }
}

if (!customElements.get("ns-product-gallery")) {
  customElements.define("ns-product-gallery", NsProductGallery);
}